import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

const DANGEROUS_PROTOCOLS = /^(javascript|data|vbscript):/i;

const REDIRECT_PARAMS = new Set([
  'returnurl', 'redirect', 'next', 'callback', 'url',
  'return', 'redirect_uri', 'redirecturl', 'return_to',
]);

const MAX_DECODE_ITERATIONS = 5;

function recursiveDecode(value: string): string | null {
  let decoded = value;

  for (let i = 0; i < MAX_DECODE_ITERATIONS; i++) {
    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return null;
    }

    if (next === decoded) {
      return decoded;
    }

    decoded = next;
  }

  return decoded;
}

function isExternalUrl(value: string): boolean {
  if (value.startsWith('//')) {
    return true;
  }

  try {
    const url = new URL(value, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function containsMaliciousValue(params: Record<string, string>): boolean {
  return Object.entries(params).some(([key, value]) => {
    if (typeof value !== 'string') {
      return false;
    }

    const decoded = recursiveDecode(value);

    if (decoded === null) {
      return true;
    }

    const trimmed = decoded.trim();

    if (DANGEROUS_PROTOCOLS.test(trimmed)) {
      return true;
    }

    if (REDIRECT_PARAMS.has(key.toLowerCase()) && isExternalUrl(trimmed)) {
      return true;
    }

    return false;
  });
}

export const urlGuard: CanActivateFn = (route) => {
  const router = inject(Router);

  if (containsMaliciousValue(route.queryParams)) {
    router.navigate(['/sign-in']);
    return false;
  }

  return true;
};
